import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from 'components/Icon';
import { H1, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { useOnClickOutside } from 'hooks';
import { IEmployerSite } from 'types';
import { SiteSelectorAppearance } from './types';

interface ISiteSelectorProps {
  options: IEmployerSite[];
  currentSite?: IEmployerSite;
  onChange: (selected: IEmployerSite) => void;
  appearance?: SiteSelectorAppearance;
  allSitesEnabled?: boolean;
}

const Layout = styled.div<{ $appearance: SiteSelectorAppearance }>`
  position: relative;
  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      width: 100%;
      background-color: ${theme.color.white};
    `}
`;

const DropDownSelector = styled.div<{ $isOpen: boolean; $appearance: SiteSelectorAppearance }>`
  display: grid;
  grid-template-columns: auto ${toRem(24)};
  align-items: center;
  grid-gap: ${toRem(8)};
  cursor: pointer;

  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT
      ? css`
          border-radius: ${toRem(4)};
          border: ${toRem(1)} solid ${theme.color.neutral[200]};
          padding: ${toRem(12)} ${toRem(16)};
          width: 100%;
        `
      : css`
          grid-template-columns: auto 1fr;
        `}

  h1 {
    font-size: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? toRem(14) : `clamp(${toRem(20)}, 5vw, ${toRem(40)})`)};
    font-weight: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? theme.font.weight.bold : 300)};
    color: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? theme.color.neutral[900] : theme.color.white)};
    margin: 0;
    text-align: left;

    @media (max-width: ${theme.breakpoint.small}px) {
      ${({ $appearance }) =>
        $appearance === SiteSelectorAppearance.PAGE_HEADER &&
        css`
          text-align: center;
        `}
    }
  }

  svg {
    height: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? toRem(16) : toRem(24))} !important;
    width: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? toRem(16) : toRem(24))} !important;
    margin-left: ${toRem(10)};
    fill: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? theme.color.neutral[500] : theme.color.white)};

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

const ItemWrapper = styled.div<{ $isSelected?: boolean; $appearance: SiteSelectorAppearance }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${toRem(4)};
  width: 100%;

  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      p {
        color: ${theme.color.neutral[500]};
      }
    `}

  h1 {
    word-break: break-word;

    ${({ $isSelected, $appearance }) =>
      $isSelected &&
      $appearance === SiteSelectorAppearance.DEFAULT &&
      css`
        font-weight: ${theme.font.weight.bold} !important;
      `}
  }
`;

const Menu = styled.div<{ top: number; $appearance: SiteSelectorAppearance }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: ${({ top, $appearance }) => `${top + ($appearance === SiteSelectorAppearance.PAGE_HEADER ? 8 : 0)}px`};
  left: 0;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(4)};
  box-shadow: ${theme.shadow.dialog};
  width: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? '100%' : toRem(360))};
  padding: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? `0 ${toRem(4)}` : `${toRem(8)} ${toRem(4)}`)};
  z-index: ${theme.zIndex.nav - 1};
  gap: ${toRem(4)};
  max-height: ${toRem(242)};
  overflow-y: auto;

  @media (max-width: ${theme.breakpoint.small}px) {
    ${({ $appearance }) =>
      $appearance === SiteSelectorAppearance.PAGE_HEADER &&
      css`
        left: 50%;
        transform: translateX(-50%);
        width: calc(100vw - ${toRem(32)});
      `}
  }
`;

const MenuItemWrapper = styled.div<{ $appearance: SiteSelectorAppearance }>`
  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      padding: ${toRem(4)};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    `}
`;

const MenuItem = styled.div<{ $highlighted?: boolean; $appearance: SiteSelectorAppearance }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${toRem(12)};
  color: ${theme.color.neutral[900]};
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.regular};
  line-height: ${toRem(20)};
  border-radius: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? toRem(4) : toRem(8))};
  background-color: ${({ $highlighted }) => ($highlighted ? theme.color.neutral[50] : 'transparent')};
  gap: ${toRem(4)};

  h1 {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[900]};
    margin: 0;
    text-align: left;
  }

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.neutral[50]};
  }

  svg {
    position: relative;
    overflow: visible;
  }
`;

const SiteSelector: React.FC<ISiteSelectorProps> = ({ currentSite, options, onChange, appearance = SiteSelectorAppearance.DEFAULT, allSitesEnabled = false }) => {
  const [open, setOpen] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const siteSelectRef = useRef<HTMLDivElement>(null);
  const dropDownSelectorRef = useRef<HTMLDivElement>(null);
  const listboxId = 'site-select-listbox';

  useOnClickOutside(siteSelectRef, () => setOpen(false));

  const updateMenuPosition = () => {
    if (dropDownSelectorRef.current) {
      const rect = dropDownSelectorRef.current.getBoundingClientRect();
      setMenuTop(rect.height);
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;

      const optionLength = allSitesEnabled ? options.length + 1 : options.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % optionLength);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + optionLength) % optionLength);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < optionLength) {
          onChange(options[highlightedIndex]);
          setOpen(false);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    },
    [highlightedIndex, open, onChange, options]
  );

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open, handleKeyDown]);

  useEffect(() => {
    const el = itemRefs.current[highlightedIndex];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const buildAddress = (site: IEmployerSite) => (site?.address ? `${site.address.address1}, ${site.address.city}, ${site.address.state}, ${site.address.zipCode}` : '');

  return (
    <Layout ref={siteSelectRef} $appearance={appearance} data-cy="site-selector">
      <DropDownSelector
        ref={dropDownSelectorRef}
        onClick={() => {
          setOpen(!open);
          if (!open) setHighlightedIndex(-1);
        }}
        $isOpen={open}
        $appearance={appearance}
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={highlightedIndex >= 0 ? `site-option-${highlightedIndex}` : undefined}
        tabIndex={0}
      >
        <ItemWrapper $isSelected $appearance={appearance}>
          <H1>{currentSite?.name || 'All Sites'}</H1>
          {appearance === SiteSelectorAppearance.DEFAULT && <Text size="xsmall">{buildAddress(currentSite)}</Text>}
        </ItemWrapper>
        <Icon icon="Dropdown" />
      </DropDownSelector>

      {open && (
        <Menu top={menuTop} role="listbox" id={listboxId} $appearance={appearance}>
          {allSitesEnabled && (
            <MenuItemWrapper $appearance={appearance}>
              <MenuItem
                id="site-option-all-sites"
                role="option"
                aria-selected={!currentSite}
                $highlighted={!currentSite}
                $appearance={appearance}
                ref={(el) => {
                  itemRefs.current[0] = el;
                }}
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
              >
                <ItemWrapper $isSelected={!currentSite} $appearance={appearance}>
                  <H1>All Sites</H1>
                </ItemWrapper>
                {!currentSite && <Icon icon="Success" width={16.5} height={16.5} fill={theme.color.secondary[400]} />}
              </MenuItem>
            </MenuItemWrapper>
          )}
          {options.map((option, idx) => {
            const index = allSitesEnabled ? idx + 1 : idx;
            return (
              <MenuItemWrapper key={option.id} $appearance={appearance}>
                <MenuItem
                  id={`site-option-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  $highlighted={index === highlightedIndex}
                  $appearance={appearance}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <ItemWrapper $isSelected={option.id === currentSite?.id} $appearance={appearance}>
                    <H1>{option.name}</H1>
                    {appearance === SiteSelectorAppearance.DEFAULT && <Text size="xsmall">{buildAddress(option)}</Text>}
                  </ItemWrapper>
                  {option.id === currentSite?.id && <Icon icon="Success" width={16.5} height={16.5} fill={theme.color.secondary[400]} />}
                </MenuItem>
              </MenuItemWrapper>
            );
          })}
        </Menu>
      )}
    </Layout>
  );
};

export default SiteSelector;
